import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { QueryObserverResult } from 'react-query';
import { FontWeight, NoticeModal, Text, TextVariant } from '@lili/lili-frontend-common';
import Heading from '../../../generic-components/Heading';
import LiliTabs from '../../../DesignSystem/LiliTabs/LiliTabs';
import { FORM_TYPES } from '../../Supplier/manageComponents/FormTypeSwitch';
import { isRequireDataForVendorValid, MoveMoneySteps, MoveMoneyTypes, vendorManagerContentMap } from '../consts';
import { FORM_FIELDS } from '../../../components/form/dynamicForm/consts';
import { NavigationHeader } from '../components/NavigationHeader';
import { VIEWS_MAP } from '../../../router/constants';
import { SupplierEntityTypes } from '../../../components/VendorManager/interfaces';
import { useMoveMoneyLogic } from '../hooks/useMoveMoneyLogic';
import { UpdateVendorFormContainer } from '../../UpdateVendor';
import { FormTypes } from '../../UpdateVendor/interfaces';
import { ISupplierModel } from '../../Supplier/types';
import { useCreateSupplier } from '../../Supplier/hooks/useCreateSupplier';
import LiliToast, { LiliToastDefaultConfig } from '../../../DesignSystem/LiliToast/LiliToast';
import { useGetSupplierById } from '../../Supplier/hooks/useGetSupplierById';
import { useGetSupplierData } from '../../Supplier/hooks/useGetSupplierData';
import { ReactComponent as AlertIcon } from '../../../assets/icons/svg/colored-icons/ic-colored-alert.svg';
import { useDeleteSupplier } from '../../Supplier/hooks/useDeleteSupplier';
import LiliNotification, { LiliNotificationType } from '../../../DesignSystem/LiliNotification/LiliNotification';
import { ACTION_MODAL_TYPES, ActionModal } from '../../../DesignSystem/ActionModal';

export const VendorContext = React.createContext<{formType: string}>({ formType: FORM_TYPES.BUSINESS });

export const VendorUpdate = () => {
  const { getSupportedCountries, moveMoneyData, setMoveMoneyData, resetMoveMoneyData, getMoveMoneyData } = useMoveMoneyLogic();
  const [isAddressMissMatch, setIsAddressMissMatch] = useState<boolean>(false);
  const [isDeleteVendor, setIsDeleteVendor] = useState<boolean>(false);

  const { mutateAsync: createSupplier, isLoading: isLoadingSupplier } = useCreateSupplier(() => { setIsAddressMissMatch(true); });
  const { refetch: fetchVendorData, isLoading: isFetchingVendor } = useGetSupplierById({
    supplierExternalId: moveMoneyData?.vendorData?.supplierId || '',
  });
  const { refetch: getSupplierList, isRefetching: isRefetchingVendorList, isLoading: isLoadingSupplierList } = useGetSupplierData({

  });
  const { mutateAsync: postDeleteSupplier, isLoading: isLoadingDelete } = useDeleteSupplier((errMsg: string) => {
    toast(<LiliNotification undismissable type={LiliNotificationType.ERROR} text={errMsg} id='vendorDetailsError' />, {
      ...LiliToastDefaultConfig,
    });
  });

  const [activeFormType, setActiveFormType] = useState<FormTypes>(FormTypes.BUSINESS);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const navigate = useNavigate();

  const { moveType } = useParams();
  const { t } = useTranslation();

  const handleBackButton = () => {
    if ((moveMoneyData.isVendorUpdateMandatory || moveMoneyData.isUpdateNameRequired) && moveMoneyData.billId) {
      resetMoveMoneyData();
      navigate(`/${VIEWS_MAP.BILLS.BASE}/${VIEWS_MAP.BILLS.PAY_BILL.BASE}/${moveMoneyData.billId}`);
      return;
    }
    if (!isRequireDataForVendorValid(moveMoneyData.vendorData, moveType as MoveMoneyTypes) || moveMoneyData.isVendorUpdateMandatory || moveMoneyData.isUpdateNameRequired) {
      resetMoveMoneyData();
      setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.VENDOR_PICKER, isVendorUpdateMandatory: false, isUpdateNameRequired: false });
      return;
    }
    if (!moveMoneyData?.supplierList?.length) {
      setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.INTRO, isVendorUpdateMandatory: false, isUpdateNameRequired: false });
    } else if (moveMoneyData.isEditModeFromAddressBook) {
      setMoveMoneyData({
        ...moveMoneyData,
        activeStepId: MoveMoneySteps.VENDOR_PICKER,
        isEditModeFromAddressBook: false,
        isVendorUpdateMandatory: false,
        isUpdateNameRequired: false,
        vendorData: undefined,
      });
    } else {
      setMoveMoneyData({
        ...moveMoneyData,
        activeStepId: moveMoneyData.isEditMode ? MoveMoneySteps.REVIEW : MoveMoneySteps.VENDOR_PICKER,
        isVendorUpdateMandatory: false,
        isUpdateNameRequired: false,
      });
    }
  };

  const handleCloseFlow = () => {
    if ((moveMoneyData.isVendorUpdateMandatory || moveMoneyData.isUpdateNameRequired) && moveMoneyData.billId) {
      resetMoveMoneyData();
      navigate(`/${VIEWS_MAP.BILLS.BASE}/${VIEWS_MAP.BILLS.PAY_BILL.BASE}/${moveMoneyData.billId}`);
    } else {
      setMoveMoneyData({
        ...moveMoneyData,
        activeStepId: MoveMoneySteps.VENDOR_PICKER,
        isEditModeFromAddressBook: false,
        isVendorUpdateMandatory: false,
      });
    }
  };

  const moveToNextStep = () => {
    if (moveMoneyData.isEditMode) {
      setMoveMoneyData({
        ...getMoveMoneyData(),
        activeStepId: (moveMoneyData.isVendorUpdateMandatory && moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) ? MoveMoneySteps.PURPOSE_OF_TRANSFER : MoveMoneySteps.REVIEW,
        isVendorUpdateMandatory: false,
        isUpdateNameRequired: false,
      });
    } else if (moveMoneyData.isVendorUpdateMandatory || moveMoneyData.isUpdateNameRequired) {
      setMoveMoneyData({
        ...getMoveMoneyData(),
        activeStepId: MoveMoneySteps.AMOUNT,
        isVendorUpdateMandatory: false,
        isUpdateNameRequired: false,
      });
    } else {
      setMoveMoneyData({
        ...getMoveMoneyData(),
        activeStepId: MoveMoneySteps.VENDOR_PICKER,
        isVendorUpdateMandatory: false,
        isUpdateNameRequired: false,
      });
    }
  };

  const getVendors = async () => {
    const supplierResponse = await getSupplierList();
    const supplierList: ISupplierModel[] = supplierResponse?.data?.suppliers;
    setMoveMoneyData({ ...getMoveMoneyData(), supplierList });
  };

  const handleSubmit = async (formData: ISupplierModel) => {
    await createSupplier({ payload: formData });
    fetchVendorData().then((res: QueryObserverResult<ISupplierModel>) => {
      const vendorData = res.data;
      setMoveMoneyData({ ...moveMoneyData, vendorData });
      getVendors().then(() => moveToNextStep());
    });
    toast(
      <LiliToast
        text={`${formData.name} ${moveMoneyData?.vendorData?.supplierId ? 'Updated' : 'Created'}`}
        id='updateConfirmation'
      />,
      { ...LiliToastDefaultConfig },
    );
  };

  const handleDelete = async () => {
    await postDeleteSupplier({ supplierExternalId: moveMoneyData?.vendorData?.supplierId?.toString() || '' });
    getVendors().then(() => {
      setMoveMoneyData({
        ...getMoveMoneyData(),
        activeStepId: MoveMoneySteps.VENDOR_PICKER,
        vendorData: undefined,
        isVendorUpdateMandatory: false,
        isUpdateNameRequired: false,
      });
      toast(<LiliToast text={`${moveMoneyData.vendorData?.name} Deleted`} id='updateConfirmation' />, {
        ...LiliToastDefaultConfig,
      });
    });
  };

  useEffect(() => {
    const elLabelBusinessName = document.getElementById(`elLabel-${FORM_FIELDS.BUSINESS_NAME}`);
    if (elLabelBusinessName) {
      elLabelBusinessName.textContent =
        activeFormType === FORM_TYPES.BUSINESS
          ? t('RECIPIENT_MANAGER_NAME_LABELS.BUSINESS')
          : t('RECIPIENT_MANAGER_NAME_LABELS.INDIVIDUAL');
    }
  }, [activeFormType]);

  useEffect(() => {
    if (moveMoneyData.preFilledBusinessName) {
      const elLabelBusinessName = document.getElementById(`elInput-${FORM_FIELDS.BUSINESS_NAME}`);
      elLabelBusinessName?.focus();
      elLabelBusinessName?.blur();
    }

    getSupportedCountries();

    if (moveMoneyData.vendorData?.supplierEntityType === SupplierEntityTypes.INDIVIDUAL) {
      setActiveTabIndex(1);
    }
    setActiveFormType(moveMoneyData.vendorData?.supplierEntityType as FormTypes || FormTypes.BUSINESS);
    setMoveMoneyData({ ...moveMoneyData, preFilledBusinessName: '' });
  }, []);

  return (
    <div className='fixed left-0 top-0 w-full h-[100dvh] bg-bw00 z-50 flex flex-col'>
      <NavigationHeader shouldDirectClose onBack={handleBackButton} onClose={handleCloseFlow} />

      <Heading
        type='HTML'
        title={
          (moveMoneyData.isVendorUpdateMandatory || moveMoneyData.isUpdateNameRequired)
            ? t('MOVE_MONEY.STEPS.VENDOR_MANAGER.EDIT_VENDOR_MANDATORY')
            : moveMoneyData.vendorData?.supplierId
              ? t('MOVE_MONEY.STEPS.VENDOR_MANAGER.EDIT_VENDOR')
              : t(vendorManagerContentMap[moveType as keyof typeof vendorManagerContentMap].content)
        }
        className='mx-auto text-center mb-[28px] font-metricR text-bw08 text-[24px]'
      />
      {!moveMoneyData.isVendorUpdateMandatory && !moveMoneyData.isUpdateNameRequired && (
        <div className='w-fit mx-auto mb-[28px]'>
          <LiliTabs
            activeIndex={activeTabIndex}
            onTabIndexChanged={(idx, id) => {
              setActiveTabIndex(idx);
              setActiveFormType(id as FormTypes || FormTypes.BUSINESS);
            }}
            tabs={[
              { title: 'Business', content: '', id: FormTypes.BUSINESS },
              { title: 'Individual', content: '', id: FormTypes.INDI },
            ]}
          />
        </div>
      )}

      <div className='flex relative grow'>
        <UpdateVendorFormContainer
          onVendorSubmit={handleSubmit}
          onVendorDelete={() => setIsDeleteVendor(true)}
          vendorData={moveMoneyData.vendorData}
          isVendorUpdateMandatory={moveMoneyData.isVendorUpdateMandatory}
          formType={activeFormType as FormTypes}
          moveType={moveType as MoveMoneyTypes}
          billId={moveMoneyData.billId}
          isEditMode={moveMoneyData.isEditMode}
          isLoadingDelete={isLoadingDelete}
          isLoading={isLoadingSupplier || isFetchingVendor || isLoadingSupplierList || isRefetchingVendorList}
          initialBusinessName={moveMoneyData.preFilledBusinessName}
          isUpdateNameRequired={moveMoneyData.isUpdateNameRequired}
        />
      </div>

      {isAddressMissMatch &&
        <NoticeModal
          SvgComponent={AlertIcon}
          title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDRESS_MISS_MATCH_MODAL.TITLE')}
          id='NoticeModal'
          onClickOutside={() => { setIsAddressMissMatch(false); }}
          content={
            <div className='flex flex-col items-center text-center mb-[48px] mt-[8px] gap-[8px]'>
              <Text className='w-[340px]' variant={TextVariant.H10} fontWeight={FontWeight.MEDIUM} title='' id=''>{t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDRESS_MISS_MATCH_MODAL.CONTENT_1')}</Text>
              <Text className='w-[404px]' variant={TextVariant.H10} fontWeight={FontWeight.REGULAR} title='' id=''>{t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDRESS_MISS_MATCH_MODAL.CONTENT_2')}</Text>
            </div>
          }
          buttonText={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDRESS_MISS_MATCH_MODAL.CTA')}
          onSubmit={() => { setIsAddressMissMatch(false); }}
        />}

      {isDeleteVendor && <ActionModal
        isLoading={isLoadingDelete || isLoadingSupplier || isFetchingVendor || isLoadingSupplierList || isRefetchingVendorList}
        modalType={ACTION_MODAL_TYPES.DESTRUCTIVE}
        description={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.DELETE_MODAL_CONTENT', {
          replace: { vendorName: moveMoneyData.vendorData?.name },
        })}
        title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.DELETE_MODAL_HEADER', {
          replace: { vendorName: moveMoneyData.vendorData?.name },
        })}
        mainButtonTitle={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.DELETE')}
        cancelButtonTitle={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.CANCEL')}
        onSubmit={handleDelete}
        onCancel={() => setIsDeleteVendor(false)}
      />}

    </div>
  );
};
